import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormAutocomplete } from '@ipfabric/ui-components/FormAutocomplete/FormAutocomplete';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  type TableRequestBody,
  VALID_ATTRIBUTE_KEY_NAME_REGEX,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useApiTableAutocomplete } from 'components/FormikTypeaheadInput/useApiTableAutocomplete';
import { useLocale } from 'containers/App/LocaleContext';
import type { DeviceAttributesRowData } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/DeviceAttributesRowData';
import type { FC } from 'react';
import { FormProvider, type SubmitHandler } from 'react-hook-form';
import type { ApiRequestParams } from 'utils/request/ApiRequestParams';
import { useApi } from 'utils/request/useApi';

type CreateGlobalAttributeRequestBody = {
  name: string;
  sn: string;
  value: string;
};

type OverwriteGlobalAttributeRequestBody = {
  name: string;
  sn: string;
  value: string;
};

export interface GlobalAttributeFormData {
  name: string;
  sn: string;
  value: string;
}

interface DeviceAttributeSnOption {
  sn: string;
  hostname: string;
}

interface DeviceAttributeOption {
  keyName: string;
}

interface Props {
  attribute: DeviceAttributesRowData | null;
  onCancel: () => void;
  onSave: () => void;
}

export const DeviceAttributesForm: FC<Props> = (props) => {
  const formMsg = useLocale().settings.deviceAttributes.form;
  const api = useApi();

  const initialValues: GlobalAttributeFormData = {
    name: props.attribute?.name ?? '',
    sn: props.attribute?.sn ?? '',
    value: props.attribute?.value.toString() ?? '',
  };

  const methods = useCustomForm<GlobalAttributeFormData>({
    defaultValues: initialValues,
  });
  const { control, handleSubmit, resetField, formState, setValue } = methods;

  const snAutocomplete = useApiTableAutocomplete<DeviceAttributeSnOption>({
    snapshot: false,
    createRequest: (
      searchEvent,
    ): ApiRequestParams<TableRequestBody<DeviceAttributeSnOption>> => ({
      url: '/tables/inventory/discovery-history',
      data: {
        columns: ['sn', 'hostname'],
        filters: {
          or: [
            { sn: ['like', searchEvent.inputValue] },
            { hostname: ['like', searchEvent.inputValue] },
          ],
        },
      },
    }),
  });

  const attributeAutocomplete = useApiTableAutocomplete<DeviceAttributeOption>({
    snapshot: false,
    // we expect there will be actually few device attributes in most cases
    fetchWithEmptyQuery: true,
    createRequest: (
      searchEvent,
    ): ApiRequestParams<TableRequestBody<DeviceAttributeOption>> => ({
      url: '/tables/attributes/settings/keys',
      data: {
        columns: ['keyName'],
        filters: searchEvent.inputValue
          ? { keyName: ['like', searchEvent.inputValue] }
          : {},
      },
    }),
  });

  const onSubmit: SubmitHandler<GlobalAttributeFormData> = async (data) => {
    if (props.attribute === null) {
      await api.request<void, CreateGlobalAttributeRequestBody>({
        method: 'POST',
        url: '/attributes/global',
        data,
      });
    } else {
      await api.request<void, OverwriteGlobalAttributeRequestBody>({
        method: 'PUT',
        url: `/attributes/global/${encodeURIComponent(props.attribute.id)}`,
        data,
      });
    }
    props.onSave();
  };

  return (
    <FormProvider {...methods}>
      <SidePanel
        title={
          props.attribute === null ? 'Create new attribute' : 'Edit attribute'
        }
        closeOnBackdropClick={false}
        onClose={props.onCancel}
        footer={({ closeModal }) => (
          <div className='flex gap-xs'>
            <Button
              type='submit'
              disabled={!formState.isDirty}
              loading={
                snAutocomplete.loading ||
                attributeAutocomplete.loading ||
                formState.isSubmitting
              }
              variant='primary'
              onClick={handleSubmit(onSubmit)}
              className='flex-1'
              icon='fa-save'
            >
              {props.attribute === null ? 'Add attribute' : 'Save changes'}
            </Button>
            <Button variant='outline' onClick={closeModal} className='flex-1'>
              Close
            </Button>
          </div>
        )}
      >
        <form>
          <Card className='flex flex-col gap p-sm'>
            <FormAutocomplete.Record<
              GlobalAttributeFormData,
              DeviceAttributeSnOption
            >
              control={control}
              getOptionLabel={({ sn, hostname }) => `${sn} /
                  ${hostname ?? formMsg.hostnameUnknown}`}
              isOptionEqualToValue={(option, value) => option.sn === value}
              label={formMsg.sn}
              loading={snAutocomplete.loading}
              name='sn'
              onChange={(value) => setValue('sn', value)}
              onSearch={snAutocomplete.search}
              optionValueName='sn'
              options={snAutocomplete.content}
              rules={{
                required: formMsg.validation.snRequired,
              }}
            />
            <FormAutocomplete.Record<
              GlobalAttributeFormData,
              DeviceAttributeOption
            >
              control={control}
              getOptionLabel={prop('keyName')}
              isOptionEqualToValue={(option, value) => option.keyName === value}
              label={formMsg.attribute}
              loading={attributeAutocomplete.loading}
              name='name'
              onChange={(value) => setValue('name', value)}
              onSearch={attributeAutocomplete.search}
              optionValueName='keyName'
              options={attributeAutocomplete.content}
              rules={{
                required: formMsg.validation.nameRequired,
                validate: (value) =>
                  VALID_ATTRIBUTE_KEY_NAME_REGEX.test(value) ||
                  formMsg.validation.nameInvalid,
              }}
            />

            <FormTextField
              control={control}
              name='value'
              type='text'
              label={formMsg.value}
              onClear={
                formState.dirtyFields.value
                  ? () => {
                      resetField('value');
                    }
                  : undefined
              }
              rules={{
                required: formMsg.validation.valueRequired,
              }}
            />
          </Card>
        </form>
      </SidePanel>
    </FormProvider>
  );
};
